import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, WifiOff, KeyRound } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../stores/authStore.js';
import { useRecordatoriosHoy } from '../../features/recordatorios/useRecordatorios.js';
import { useWhatsAppEstado } from '../../features/whatsapp/useWhatsApp.js';
import { useCambiarPassword } from '../../features/usuarios/useUsuarios.js';

export default function Header({ title, back }) {
  const [modalPassword, setModalPassword] = useState(false);
  const usuario = useAuthStore((s) => s.usuario);
  const esAdmin = usuario?.rol === 'admin';
  const { data: hoy = [] } = useRecordatoriosHoy();
  const pendientes = hoy.filter((r) => r.estado === 'activo').length;
  const { data: waEstado } = useWhatsAppEstado({ enabled: esAdmin });
  const waDesconectado = esAdmin && waEstado?.estado && waEstado.estado !== 'open' && waEstado.estado !== 'no_configurado';

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        {back && (
          <button
            onClick={back.onClick}
            className="text-gray-400 hover:text-gray-600 text-sm transition-colors flex items-center gap-1"
          >
            ← {back.label}
          </button>
        )}
        <h1 className="font-['Raleway'] font-bold text-[#0D2B5C] text-xl">{title}</h1>
      </div>

      <div className="flex items-center gap-4">
        {waDesconectado && (
          <Link
            to="/whatsapp"
            className="flex items-center gap-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-2.5 py-1.5 rounded-md hover:bg-red-100 transition-colors"
            title="WhatsApp desconectado — click para reconectar"
          >
            <WifiOff size={13} />
            WhatsApp desconectado
          </Link>
        )}
        {pendientes > 0 && (
          <Link
            to="/recordatorios"
            className="relative flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
            title={`${pendientes} recordatorio${pendientes > 1 ? 's' : ''} para hoy`}
          >
            <Bell size={18} />
            <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
              {pendientes}
            </span>
          </Link>
        )}
        <button
          onClick={() => setModalPassword(true)}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#0D2B5C] font-medium uppercase tracking-wide transition-colors"
          title="Cambiar contraseña"
        >
          <span>{usuario?.rol}</span>
          <KeyRound size={13} />
        </button>
      </div>

      {modalPassword && (
        <ModalCambiarPassword onClose={() => setModalPassword(false)} />
      )}
    </header>
  );
}

const cambiarPasswordSchema = z.object({
  password_actual: z.string().min(1, 'Requerido'),
  password_nuevo: z.string().min(8, 'Mínimo 8 caracteres'),
  confirmar: z.string().min(1, 'Requerido'),
}).refine((d) => d.password_nuevo === d.confirmar, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmar'],
});

const inputCls = "border border-gray-200 rounded-md px-3 py-2 text-sm font-['Inter'] focus:outline-none focus:ring-2 focus:ring-[#0D2B5C] w-full";

function ModalCambiarPassword({ onClose }) {
  const mutation = useCambiarPassword();
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(cambiarPasswordSchema),
  });

  function onSubmit({ password_actual, password_nuevo }) {
    mutation.mutate({ password_actual, password_nuevo }, { onSuccess: onClose });
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm mx-4 flex flex-col gap-5">
        <h2 className="font-['Raleway'] font-bold text-[#0D2B5C] text-lg">Cambiar contraseña</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="font-['Raleway'] text-[#1E9BE8] text-xs font-semibold tracking-widest uppercase">Contraseña actual</label>
            <input {...register('password_actual')} type="password" className={inputCls} />
            {errors.password_actual && <span className="text-red-500 text-xs">{errors.password_actual.message}</span>}
          </div>

          <div className="flex flex-col gap-1">
            <label className="font-['Raleway'] text-[#1E9BE8] text-xs font-semibold tracking-widest uppercase">Nueva contraseña</label>
            <input {...register('password_nuevo')} type="password" className={inputCls} placeholder="Mínimo 8 caracteres" />
            {errors.password_nuevo && <span className="text-red-500 text-xs">{errors.password_nuevo.message}</span>}
          </div>

          <div className="flex flex-col gap-1">
            <label className="font-['Raleway'] text-[#1E9BE8] text-xs font-semibold tracking-widest uppercase">Confirmar contraseña</label>
            <input {...register('confirmar')} type="password" className={inputCls} />
            {errors.confirmar && <span className="text-red-500 text-xs">{errors.confirmar.message}</span>}
          </div>

          {mutation.error && (
            <p className="text-red-500 text-sm font-['Inter']">
              {mutation.error?.response?.data?.error || 'Error al cambiar contraseña'}
            </p>
          )}

          {mutation.isSuccess && (
            <p className="text-green-600 text-sm font-['Inter']">Contraseña actualizada correctamente.</p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 bg-[#1E9BE8] hover:bg-[#1780C5] text-white font-['Raleway'] font-bold text-sm px-6 py-3 rounded-md transition-colors min-h-[44px] disabled:opacity-50"
            >
              {mutation.isPending ? 'Guardando...' : 'Guardar'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border-2 border-[#0D2B5C] text-[#0D2B5C] hover:bg-[#0D2B5C] hover:text-white font-['Raleway'] font-bold text-sm px-6 py-3 rounded-md transition-colors min-h-[44px]"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
